import { Container, Flex } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { useResourcesInfinite } from 'datx-jsonapi-react';
import { FC, useCallback, useMemo } from 'react';

import { HubPublishersGrid } from '@/components/ui/features/societies/HubPublishersGrid/HubPublishersGrid';
import { HubsPageActions } from '@/components/ui/shared/hubs/HubsPageActions/HubsPageActions';
import { ThinConferenceSort } from '@/enums/ThinConferenceSort';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { IMetaOmnisearchProps } from '@/interfaces/search';
import { queryThinConferencesInfinite } from '@/queries/thin-conferences';
import { ThinConference } from '@/resources/ThinConference';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

interface ILibraryFiltersPageContentParams {
  sort: ThinConferenceSort;
  slug: string;
  query?: string;
  subject_subtree_of_id: string;
}

const pageSize = 12;

const sortingOptions = [
  {
    label: 'alphabeticallyAZ',
    value: ThinConferenceSort.ExtendedName,
  },
  {
    label: 'alphabeticallyZA',
    value: `-${ThinConferenceSort.ExtendedName}`,
  },
];

export const PublishersContent: FC = () => {
  const { query, sort, subject_subtree_of_id } = useQueryParams<ILibraryFiltersPageContentParams>({
    query: StringParam,
    subject_subtree_of_id: StringParam,
    sort: StringParam,
  });

  const { data: thin_conferences, error, size, setSize } = useResourcesInfinite(
    queryThinConferencesInfinite(
      { query, subject_id: subject_subtree_of_id, visible: 'true', with_library_lectures: 'true' },
      sort
    )
  );

  const { meta, ...swrExtract } = useMemo(
    () => extractSWRInfiniteData<ThinConference>(thin_conferences as Array<Response<ThinConference>>, error, size),
    [thin_conferences, error, size]
  );

  const isReachingEnd = useMemo(() => {
    if (!thin_conferences) return false;
    const lastPage = thin_conferences?.[thin_conferences.length - 1].meta as IMetaOmnisearchProps;

    return lastPage.currentPage === lastPage.totalPages;
  }, [thin_conferences]);

  const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

  return (
    <Container>
      <Flex flexDir="column" gap={4}>
        <HubsPageActions
          sortingOptions={sortingOptions}
          defaultOption={ThinConferenceSort.ExtendedName}
          totalCount={meta?.totalCount}
        />
        <HubPublishersGrid
          {...swrExtract}
          isReachingEnd={isReachingEnd}
          onLoadMore={onLoadMore}
          error={(error as unknown) as Error}
          pageSize={pageSize}
        />
      </Flex>
    </Container>
  );
};
